import { ContactInformationInput, ContactInformationInputSchema, DuplicateInfoDto, reportErrorToConsole, useGetActiveCreditAppLinkedToCellPhoneOrEmail, useNewsletterSubscription } from '@applyonline/shared'
import { FORM_ID, PhoneInput, ScrollableAlert } from '@applyonline/shared/components'
import { helpTip, useFormAutoSave } from '@applyonline/shared/utils'
import { yupResolver } from '@hookform/resolvers/yup'
import { EFinancingProgram, ELoanPurpose, EOriginSystemId } from '@if/core/types'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useTrackFunnelStepReached } from '../../../services/trackingEvents'
import { ETrackedEvents } from '../../../types/tracking'

interface Props {
  applicantData: ContactInformationInput
  onApplicantChanged: (data: ContactInformationInput) => void
  nextStepAfter: (currentStep: string) => void
  currentStep: string
  prohibedPhone?: string
  loanPurposeId: string
  firstName: string
  middleName?: string | null | undefined
  lastName: string
  languageId: number
}

const ContactInformationForm = ({
  applicantData,
  onApplicantChanged,
  nextStepAfter,
  currentStep,
  prohibedPhone,
  loanPurposeId,
  firstName,
  middleName,
  lastName,
  languageId,
}: Props) => {
  const { t } = useTranslation()

  useTrackFunnelStepReached(ETrackedEvents.ContactInformation, useParams().merchantId)

  const {
    handleSubmit,
    control,
    register,
    formState: { errors },
    subscribe,
  } = useForm<ContactInformationInput>({
    mode: 'onBlur',
    resolver: yupResolver(ContactInformationInputSchema),
    defaultValues: { ...applicantData, applicantPhoneNumber: prohibedPhone },
  })

  useFormAutoSave(subscribe, onApplicantChanged)

  useEffect(() => {
    helpTip()
  }, [])

  const [hasDuplicateLoanRequest, setHasDuplicateLoanRequest] = useState(false)
  const newsletterMutation = useNewsletterSubscription()

  const [activeCreditAppLinkedToCellPhoneOrEmail] = useGetActiveCreditAppLinkedToCellPhoneOrEmail(
    EFinancingProgram.Personal,
  )

  const onSubmit: SubmitHandler<ContactInformationInput> = async (data) => {
    const dto: DuplicateInfoDto = {
      email: data.email,
      cellPhone: data.cellPhone,
    }
      const isDuplicateRequest = await activeCreditAppLinkedToCellPhoneOrEmail(dto)
      if (!isDuplicateRequest) {
      if (data.subscribeNewsletter) {
        try {
          let newLoanPurposeId: ELoanPurpose;
          if (Object.values(ELoanPurpose).includes(loanPurposeId as ELoanPurpose) === false) {
            newLoanPurposeId = ELoanPurpose.GoodsAndServices
          }
          else {
            newLoanPurposeId = loanPurposeId as ELoanPurpose;
          }
          newsletterMutation.mutate({
            email: data.email,
            firstName: firstName,
            middleName: middleName,
            lastName: lastName,
            languageId: languageId,
            originSystemId: EOriginSystemId.Subscription,
            financingProgramId: EFinancingProgram.Personal,
            loanPurposeId: newLoanPurposeId,
          })
        } catch (error) {
          reportErrorToConsole(error)
        }
      }
      onApplicantChanged(data)
      nextStepAfter(currentStep)
    } else {
      setHasDuplicateLoanRequest(true)
    }
  }
  return (
    <section className="step-content">
      <h3 className="form-question">{t('contact.provideInformation')}</h3>
      <form onSubmit={handleSubmit(onSubmit)} id={FORM_ID}>
        <ScrollableAlert
          showAlert={hasDuplicateLoanRequest}
          type="warn"
          message={t('contact.hasActiveLoanRequest')}
          link={t('contact.icebergPhone')}
          linkText={t('contact.icebergPhone')}
          linkType="tel"
        />
        <div className="control-group-wrap" style={{ alignItems: 'start' }}>
          <PhoneInput className="mid" control={control} name="cellPhone" label={t('contact.mobilePhone')} />
          <PhoneInput className="mid" control={control} name="homePhone" label={t('contact.otherPhone')} />
        </div>
        <div className={`control-group  ${errors.email && 'error'}`}>
          <label htmlFor="email">
            {t('contact.emailAddress')}
            <span className="help-tip">
              <p>{t('contact.emailAddressInfo')}</p>
            </span>
          </label>
          <input id="email" type="text" inputMode="email" autoComplete="email" {...register('email')} maxLength={60} />
          {errors.email && <p className="error-message">{errors.email.message}</p>}
        </div>

        <div className="empty-space" />
        <div className="clarification">
          <p>
            {t('contact.newsletter')}
            <u>
              <a href={t('common.policyLink')} target="_blank" rel="noreferrer">
                {t('contact.linkText')}
              </a>
            </u>
            {t('contact.newsletterEnd')}
          </p>
        </div>
        <div className="control-group">
          <input type="checkbox" id="newsletter" {...register('subscribeNewsletter')} />
          <label htmlFor="newsletter">{t('contact.subscribe')}</label>
        </div>
      </form>
    </section>
  )
}

export default ContactInformationForm
